import { Dispatch, SetStateAction, useEffect, useState } from "react"
import {
    selectCurrentPassword,
    selectConfirmNewPassword,
    selectIsSavingChanges,
    selectNewPassword,
    setCurrentPassword,
    setConfirmNewPassword,
    setIsSavingChanges,
    setNewPassword,
} from "./slice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import classNames from "classnames"
import { ButtonPrimary } from "@nova/component-btn"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { PasswordInput } from "@nova/component-password-input"
import { checkPasswordPolicy } from "@nova/password-policy"
import {
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
    getGenericSaveableWorkspacePageContent,
} from "panels/routing/WorkspaceRoutes"

export const PasswordSettingsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({ title: "Change Password" }),
    Content: getGenericSaveableWorkspacePageContent({
        Content: PasswordSettingsPageContent,
        Footer: PasswordSettingsPageFooter,
    }),
})

function PasswordSettingsPageContent() {
    const dispatch = useAppDispatch()
    const currentPassword = useAppSelector(selectCurrentPassword)
    const newPassword = useAppSelector(selectNewPassword)
    const confirmNewPassword = useAppSelector(selectConfirmNewPassword)
    const {
        atLeast8Chars,
        atLeastOneUppercase,
        atLeastOneLowercase,
        atLeastOneNumber,
        atLeastOneSymbol,
        passwordsMatch,
    } = useAreNewPasswordsValid()
    return (
        <div className="flex">
            <small className="w-[168px] min-w-[168px] xs">PASSWORD</small>
            <div className="flex flex-col gap-4">
                <PasswordInput
                    label="Current Password *"
                    value={currentPassword}
                    onChange={(e) => dispatch(setCurrentPassword(e.target.value))}
                    className="text-sm"
                />
                <div className="w-full h-px bg-gray-700 my-4" />
                <PasswordInput
                    label="New Password *"
                    value={newPassword}
                    onChange={(e) => dispatch(setNewPassword(e.target.value))}
                    className="text-sm"
                />
                <PasswordInput
                    label="Confirm New Password *"
                    value={confirmNewPassword}
                    onChange={(e) => dispatch(setConfirmNewPassword(e.target.value))}
                    className="text-sm"
                />
                <div className="flex gap-8">
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-2 items-center">
                            <ValidIndicator valid={atLeast8Chars} />
                            <div className="text-white-400">At least 8 characters</div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <ValidIndicator valid={atLeastOneUppercase} />
                            <div className="text-white-400">At least one uppercase letter</div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <ValidIndicator valid={atLeastOneSymbol} />
                            <div className="text-white-400">At least one symbol</div>
                        </div>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-2 items-center">
                            <ValidIndicator valid={atLeastOneLowercase} />
                            <div className="text-white-400">At least one lowercase letter</div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <ValidIndicator valid={atLeastOneNumber} />
                            <div className="text-white-400">At least one number</div>
                        </div>
                        <div className="flex gap-2 items-center">
                            <ValidIndicator valid={passwordsMatch} />
                            <div className="text-white-400">Passwords match</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

function PasswordSettingsPageFooter({
    setHasUnsavedChanges,
}: {
    setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>
}) {
    const dispatch = useAppDispatch()
    const isSavingChanges = useAppSelector(selectIsSavingChanges)
    const currentPassword = useAppSelector(selectCurrentPassword)
    const newPassword = useAppSelector(selectNewPassword)
    const confirmNewPassword = useAppSelector(selectConfirmNewPassword)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    useEffect(() => {
        setHasUnsavedChanges?.(!!currentPassword || !!newPassword || !!confirmNewPassword)
    }, [currentPassword, confirmNewPassword, newPassword, setHasUnsavedChanges])
    useEffect(() => {
        return () => {
            dispatch(setCurrentPassword(""))
            dispatch(setNewPassword(""))
            dispatch(setConfirmNewPassword(""))
            setErrorMessage("")
        }
    }, [dispatch])
    const { isValid } = useAreNewPasswordsValid()
    return (
        <div className="w-full h-full flex items-center gap-2">
            <ButtonPrimary
                title="Change Password"
                disabled={!isValid || isSavingChanges}
                onClick={async () => {
                    setErrorMessage(null)
                    dispatch(setIsSavingChanges(true))
                    const response = await fetchApiGateway("patch /me/change-password", {
                        rawResponse: true,
                        body: {
                            currentPassword,
                            newPassword,
                            confirmNewPassword,
                        },
                    })
                    dispatch(setIsSavingChanges(false))
                    if (!response.ok) {
                        setErrorMessage(
                            response.status === 403
                                ? "Incorrect current password."
                                : "Something went wrong. Please try again."
                        )
                        return
                    }
                    dispatch(setCurrentPassword(""))
                    dispatch(setNewPassword(""))
                    dispatch(setConfirmNewPassword(""))
                    dispatch(
                        setGlobalNotificationContext({
                            message: "New password has been saved.",
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                }}
            />
            {errorMessage && <p className="text-sm text-red-400">{errorMessage}</p>}
        </div>
    )
}

function useAreNewPasswordsValid() {
    const currentPassword = useAppSelector(selectCurrentPassword)
    const newPassword = useAppSelector(selectNewPassword)
    const confirmNewPassword = useAppSelector(selectConfirmNewPassword)
    const policyResult = checkPasswordPolicy(newPassword)
    const passwordsMatch = !!newPassword.length && newPassword === confirmNewPassword
    const hasCurrentPassword = !!currentPassword
    return {
        ...policyResult,
        passwordsMatch,
        hasCurrentPassword,
        isValid: hasCurrentPassword && policyResult.isValid && passwordsMatch,
    }
}

function ValidIndicator({ valid }: { valid: boolean }) {
    return (
        <div className="w-5 h-5 flex justify-center items-center">
            <div
                className={classNames("w-2 h-2 flex justify-center items-center rounded-full", {
                    "bg-white-400": !valid,
                    "bg-green-500": valid,
                })}
            >
                <div className="w-1.5 h-1.5 bg-gray-900 rounded-full" />
            </div>
        </div>
    )
}
